import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import MasterPage from "../components/layout/MasterPage";
import Dashboard from "../components/layout/Dashboard";
import Analytics from "../components/Analytics";
import Tasks from "../components/Tasks";
import Profile from "../components/Profile";
import { ProtectedRoute } from "./protected-wrappers/ProtectedRoute";
import { InvoicesPage } from "../pages/InvoicesPage";
import { OrdersPage } from "../pages/OrdersPage";
import { CategorysPage } from "../pages/CategorysPage";
import { XmlProductSourcePage } from "../pages/XmlProductSourcePage";
import { XmlProductSourceAddPage } from "../pages/XmlProductSourceAddPage";
import { BatchProcessesPage } from "../pages/BatchProcessesPage";
import { BatchTextProcessesPage } from "../pages/BatchTextProcessesPage";
import { ExcelProductPage } from "../pages/ExcelProductPage";
import { SellerBrandPage } from "../pages/SellerBrandPage";
import AddProductPage from "../pages/AddProductPage";
import { ProductsPage } from "../pages/ProductsPage";
import { QuestionsPage } from "../pages/QuestionsPage";
import { InvoiceCreatePage } from "../pages/InvoiceCreatePage";
import { CategoryVariantPage } from "../pages/CategoryVariantPage";
import { SendProductBulkPage } from "../pages/SendProductBulkPage";
import { PaymentPage } from "../pages/PaymentPage";
import { OrderReportsPage } from "../pages/OrderReportsPage";
import { XmlDealerPage } from "../pages/XmlDealerPage";
import { XmlDealerUsersPage } from "../pages/XmlDealerUsersPage";
import PaymentStatusPage from "../pages/paymentStatusPage";
import { PlaceProductPage } from "../pages/PlaceProductPage";
import { StockPage } from "../pages/StockPage";
import { NoSubscriptionPage } from "../pages/NoSubscriptionPage";
import { NotificationPage } from "../pages/NotificationPage";
import { UserTasksPage } from "../pages/UserTasksPage";
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { ApplicationPage } from "../pages/AplicationPage";
import PriceRulesPage from "../pages/PriceRulesPage";
import ProductTransfers from "../components/UI/reusable-components/ProductPleacesTransferComponents/ProductTransfers";
import InvoiceCustomer from "../components/UI/reusable-components/InvoiceComponents/InvoiceCustomer";

const AuthPage = React.lazy(() => import("../pages/AuthPage"));
const SignInPage = React.lazy(() => import("../pages/SignInPage"));
const SignUpPage = React.lazy(() => import("../pages/SignUpPage"));
const ForgotPasswordPage = React.lazy(() =>
  import("../pages/ForgotPasswordPage")
);

// Rol bazlı koruma için özel bir HOC (Higher-Order Component) oluşturalım
const RoleProtectedRoute = ({ children, requiredRole }) => {
  const token = useSelector(state => state?.auth?.user?.accessToken?.token);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        
        // Role alanındaki marketplace verilerini parse et
        const roleData = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]
          .map(roleString => {
            try {
              return JSON.parse(roleString);
            } catch (parseError) {
              console.error("Role parsing error:", parseError);
              return null; // Parsing hatası durumunda null döndür
            }
          })
          .filter(item => item && item.SpecialApplicationId !== null);
        
        // SpecialApplicationId değerlerini al
        const specialAppIds = roleData.map(item => item.SpecialApplicationId);
        
        // Gerekli role sahip olup olmadığını kontrol et
        const userHasRequiredRole = requiredRole === null || specialAppIds.includes(requiredRole);
        setHasAccess(userHasRequiredRole);
      } catch (error) {
        console.error("Token çözme hatası:", error);
        setHasAccess(false);
      }
    } else {
      setHasAccess(false);
    }
    
    setIsLoading(false);
  }, [token, requiredRole]);

  if (isLoading) {
    return <div>Yetkiler kontrol ediliyor...</div>;
  }

  return hasAccess ? children : <Navigate to="/NoSubscription" replace />;
};

const AppRoutes = () => (
  <Router>
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/signin" element={<SignInPage />} />
        <Route path="/auth/signup" element={<SignUpPage />} />
        <Route path="/auth/forgotPassword" element={<ForgotPasswordPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MasterPage />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />}>
            <Route path="analytics" element={<Analytics />} />
            <Route path="tasks" element={<Tasks />} />
          </Route>
          <Route path="profile" element={<Profile />} />
          <Route path="order-reports" element={<OrderReportsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="add-product" element={<AddProductPage />} />
          <Route path="categorys" element={<CategorysPage />} />
          <Route path="price-rules" element={<PriceRulesPage />} />
          <Route path="XmlProductSource" element={<XmlProductSourcePage />} />
          <Route
            path="XmlProductSourceAddPage"
            element={<XmlProductSourceAddPage />}
          />
          <Route path="batch-processes" element={<BatchProcessesPage />} />
            <Route path="batch-text-processes" element={<BatchTextProcessesPage />} />
          <Route path="excelproduct" element={<ExcelProductPage />} />
          <Route path="sellerbrand" element={<SellerBrandPage />} />
          <Route path="invoice-create" element={<InvoiceCreatePage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="questions" element={<QuestionsPage />} />
          <Route path="user-tasks" element={<UserTasksPage />} />
          <Route path="categoryVariant" element={<CategoryVariantPage />} />
          <Route path="sendProductBulk" element={<SendProductBulkPage />} />
          <Route path="payment/*" element={<PaymentPage />} />
          <Route path="Application" element={<ApplicationPage />} />
          <Route path="Stock" element={<StockPage />} />
          <Route path="xmldealer" element={<XmlDealerPage />} />
          <Route path="xmlDealerUsersPage" element={<XmlDealerUsersPage />} />
          <Route path="paymentStatus/*" element={<PaymentStatusPage />} />
          <Route path="PlaceProducts" element={<PlaceProductPage />} />
          <Route path="Notification" element={<NotificationPage />} />
          <Route path="/NoSubscription" element={<NoSubscriptionPage />} />
          <Route path="ProductTransfers" element={<ProductTransfers />} />
          <Route path="invoiceCustomers" element={<InvoiceCustomer />} />

        </Route>
      </Routes>
    </Suspense>
  </Router>
);

export default AppRoutes;